import { Guid } from "@app/core/constants/constants";
import { RecordState } from "@app/core/constants/global-enum";
import { RecordBaseDTO } from "@app/shared/classes/record-base";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

export namespace CustomReportEditor {

  // Updated to match backend ShiftTimeRange structure
  export interface WorkingTimeRange {
    startTime: string;  // "09:00" UI format, converted to "09:00:00" for backend
    endTime: string;    // "18:00" UI format, converted to "18:00:00" for backend
  }

  export interface WorkingHoursConfig {
    enabled: boolean;
    ranges: WorkingTimeRange[];
  }

  export type PresenceSummaryAggregationMode = 'total' | 'daily';
  export type PresenceSummaryIdentityMode = 'tag' | 'trackItem';

  export interface PresenceSummaryConfig {
    aggregationMode: PresenceSummaryAggregationMode;
    identityMode: PresenceSummaryIdentityMode;
  }

  export interface ReportWidget {
    id: string;
    reportId: string;
    reportName?: string;
    name: string;
    description?: string;
    icon?: string;
    iconColorCode?: string;
    refreshInterval: number;
    displayRowCount: number;
    displayMembers?: string[];
    resizable: boolean;
    widgetKind: number;
    templateKey: string;
    rendererKey?: string;
    highlightFirst: boolean;
    openReportOnClick: boolean;
    templateConfigJson?: string;
  }

  export interface ReportWidgetTemplate {
    id: string;
    name: string;
    description?: string;
    templateKey: string;
    rendererKey: string;
    configJson?: string;
    isSystem: boolean;
    version: number;
  }

  export interface ReportWidgetData {
    widgetId?: string;
    reportId: string;
    reportName?: string;
    reportGroupKey?: string;
    displayRowCount?: number;
    templateKey?: string;
    templateConfigJson?: string;
    headers: string[];
    rows: Record<string, any>[];
    summary?: Record<string, any>;
    slots?: Record<string, any>;
    metadata?: Record<string, any>;
    errors?: string[];
    refreshedAt?: string;
  }

  export interface ReportWidgetCalculation {
    id: string;
    label: string;
    scope: 'row' | 'summary' | 'style';
    expression: string;
    format?: string;
    visible?: boolean;
    validationResult?: any;
    validationError?: string;
  }

  export type ReportWidgetRuleOperator = 'always' | 'eq' | 'notEq' | 'gt' | 'gte' | 'lt' | 'lte' | 'between' | 'contains' | 'empty' | 'notEmpty';
  export type ReportWidgetRuleTarget = 'row' | 'badge' | 'icon' | 'accent' | string;
  export type ReportWidgetRuleTargetType = 'row' | 'slot' | 'column' | 'header';
  export type ReportWidgetRuleDecoration = 'background' | 'text' | 'accentDot' | 'accentBar' | 'icon' | 'badge';
  export type ReportWidgetRulePosition = 'left' | 'right';
  export type ReportWidgetRuleColorMode = 'fixed' | 'source' | 'palette';
  export type ReportWidgetRuleColorPaletteMode = 'rowIndex' | 'sourceValue';
  export type ReportWidgetRuleBadgeLabelMode = 'fixed' | 'source';
  export type ReportWidgetSeverity = 'neutral' | 'success' | 'warning' | 'danger' | 'info';
  export type ReportWidgetRuleApplyTarget = 'widget' | 'table';

  export interface ReportWidgetConditionalRule {
    id: string;
    effectGroupId?: string;
    appliesTo?: ReportWidgetRuleApplyTarget | string;
    target: ReportWidgetRuleTarget;
    targetType?: ReportWidgetRuleTargetType | string;
    targetKey?: string;
    decoration?: ReportWidgetRuleDecoration | string;
    position?: ReportWidgetRulePosition | string;
    source: string;
    operator: ReportWidgetRuleOperator;
    value?: string;
    valueTo?: string;
    textColor?: string;
    backgroundColor?: string;
    textColorSource?: string;
    backgroundColorSource?: string;
    colorMode?: ReportWidgetRuleColorMode | string;
    colorPalette?: string[];
    colorPaletteMode?: ReportWidgetRuleColorPaletteMode | string;
    colorPaletteSource?: string;
    icon?: string;
    badgeLabel?: string;
    badgeLabelMode?: ReportWidgetRuleBadgeLabelMode | string;
    badgeLabelSource?: string;
    severity?: ReportWidgetSeverity | string;
    priority?: number;
    templateGenerated?: boolean;
    sourceTemplateKey?: string;
    templateRuleId?: string;
  }

  export interface ReportWidgetChartConfig {
    enabled: boolean;
    type: 'bar' | 'line' | 'area' | 'pie' | 'doughnut' | string;
    argumentField: string;
    valueField: string;
    valueFields?: string[];
    seriesMode?: 'single' | 'multi';
    aggregation?: 'none' | 'sum' | 'avg' | 'count' | 'min' | 'max' | string;
    rotated?: boolean;
    legendVisible?: boolean;
    labelsVisible?: boolean;
    palette?: string;
    comparison?: ReportWidgetComparisonConfig;
  }

  export interface ReportWidgetComparisonConfig {
    enabled: boolean;
    mode: 'auto';
    period: 'none' | 'previous-day' | 'previous-week' | 'previous-month' | 'previous-year' | 'previous-period';
    labelKey?: string;
  }

  export interface GridStateWrapper {
    devextremeState?: any; // DevExtreme grid state
    workingHours: WorkingHoursConfig; // Custom working hours config
    presenceSummary: PresenceSummaryConfig;
    
    // Grid display ayarları
    virtualScroll?: boolean;
    expandGridRows?: boolean;
    enableCalendarDate?: boolean;
    reportStyleRules?: ReportWidgetConditionalRule[];
    
    version: string; // For future compatibility
  }

  export interface OperationResult {
    success: boolean;
    action: 'create' | 'update' | 'delete' | 'discard' | 'refresh' | 'gridState' | 'workingHoursChanged';
    report?: CustomReport;
    error?: any;
  }

  export class CustomReport extends RecordBaseDTO {
  
    code: string;
    name: string;
    description?: string;
    colorCode?: string;
    icon?: IconProp;
    iconColorCode?: string;

    // Tek property - her şey bunun içinde
    state: GridStateWrapper;
    
    dateRange?: number;
    shareRole: number;
    isPublic: boolean;
    order: number;
    groupKey?: 'Sensor' | 'Location' | 'SensorHistory' | 'SensorAlertHistory' | 'LocationHistory' | 'AccessRuleDwell' | 'ZoneEngagement' | 'PresenceSummary' | 'ReaderPropertyHistory' | 'ReaderHealthHistory';
    new: boolean;

    // Scheduling / email
    scheduleSlotPresetId?: string;
    emailRecipients?: string;
    emailFormat?: number;
    emailLanguage?: string;
    emailIncludeFilters?: boolean;

    // Dashboard widget editor state
    widgetEnabled?: boolean;
    widgetId?: string;
    widgetTopN?: number;
    widgetTemplateKey?: string;
    widgetTemplateConfigJson?: string;
    widgetRefreshInterval?: number;
    widgetHighlightFirst?: boolean;
    widgetPublishToDashboard?: boolean;

    // gridStateChanged property'si - UI için
    get gridStateChanged(): boolean {
      return this.new; // Yeni rapor ise her zaman true
    }

    constructor() {
      super();
      // set defaults
      this.id = Guid.newGuid();
      this.code = '';
      this.name = '';
      this.description = '';
      this.colorCode = '';
      this.icon = ['fas', 'chart-line'];
      this.iconColorCode = '';
      
      // Tek state object
      this.state = {
        devextremeState: null,
        workingHours: { enabled: false, ranges: [] },
        presenceSummary: { aggregationMode: 'total', identityMode: 'tag' },
        // Grid display ayarları - default değerler
        virtualScroll: false,
        expandGridRows: false,
        enableCalendarDate: false,
        reportStyleRules: [],
        version: '1.0'
      };
      
      this.dateRange = 0;
      this.shareRole = 0;
      this.isPublic = false;
      this.order = 0;
      this.groupKey = 'Sensor';
      this.new = false;

      this.scheduleSlotPresetId = undefined;
      this.emailRecipients = '';
      this.emailFormat = 1;
      this.emailLanguage = 'en';
      this.emailIncludeFilters = false;

      this.widgetEnabled = false;
      this.widgetId = undefined;
      this.widgetTopN = 5;
      this.widgetTemplateKey = 'top-list';
      this.widgetTemplateConfigJson = '';
      this.widgetRefreshInterval = 60;
      this.widgetHighlightFirst = true;
      this.widgetPublishToDashboard = true;
    }

    // Sadece gridState - database için
    get gridState(): string {
      return JSON.stringify(this.state);
    }

    set gridState(value: string) {
      try {
        this.state = JSON.parse(value || '{}');
        // Ensure default structure
        if (!this.state.workingHours) {
          this.state.workingHours = { enabled: false, ranges: [] };
        }
        if (!this.state.presenceSummary) {
          this.state.presenceSummary = { aggregationMode: 'total', identityMode: 'tag' };
        }
        if (!this.state.presenceSummary.aggregationMode) {
          this.state.presenceSummary.aggregationMode = 'total';
        }
        if (!this.state.presenceSummary.identityMode) {
          this.state.presenceSummary.identityMode = 'tag';
        }
        if (!this.state.version) {
          this.state.version = '1.0';
        }
        // Grid display ayarları için default değerler
        if (this.state.virtualScroll === undefined) {
          this.state.virtualScroll = false;
        }
        if (this.state.expandGridRows === undefined) {
          this.state.expandGridRows = false;
        }
        if (this.state.enableCalendarDate === undefined) {
          this.state.enableCalendarDate = false;
        }
        if (!Array.isArray(this.state.reportStyleRules)) {
          this.state.reportStyleRules = [];
        }
      } catch {
        this.state = {
          devextremeState: null,
          workingHours: { enabled: false, ranges: [] },
          presenceSummary: { aggregationMode: 'total', identityMode: 'tag' },
          // Grid display ayarları - default değerler
          virtualScroll: false,
          expandGridRows: false,
          enableCalendarDate: false,
          reportStyleRules: [],
          version: '1.0'
        };
      }
    }

  }

}
